import { FC, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { PortalProps } from './types';
import { useTooltipPortal } from './use-tooltip-portal';

const findScrollParent = (element: HTMLElement | null): HTMLElement | null => {
  if (!element) return null;
  const overflowY = window.getComputedStyle(element).overflowY;
  if (overflowY === 'auto' || overflowY === 'scroll') {
    return element;
  }
  return findScrollParent(element.parentElement);
};

export const TooltipPortal: FC<PortalProps> = (props) => {
  const {
    children,
    className,
    onClose,
    triggerRef,
    behavior,
    closeOnContentClick,
    offset,
    position,
    customTrigger,
    isOpen,
  } = props;

  const tooltipRef = useRef<HTMLDivElement>(null);
  const trigger = triggerRef.current;

  useEffect(() => {
    const scrollParent = findScrollParent(trigger);
    const bodyWrapper = document.getElementById('body');
    if (!scrollParent || !bodyWrapper) return;

    const timeoutId = setTimeout(() => {
      scrollParent.addEventListener('scroll', onClose);
    }, 100);
    bodyWrapper.addEventListener('scroll', onClose);

    return () => {
      clearTimeout(timeoutId);
      scrollParent.removeEventListener('scroll', onClose);
      bodyWrapper.removeEventListener('scroll', onClose);
    };
  }, [trigger]);

  useTooltipPortal({
    triggerRef,
    tooltipRef,
    onClose,
    behavior,
    offset,
    position,
    isOpen,
  });

  if (!trigger) return null;

  return createPortal(
    <div className={className} ref={tooltipRef} onClick={() => closeOnContentClick && onClose()}>
      {children}
    </div>,
    customTrigger || trigger,
  );
};
